import React, { useMemo, useState } from "react"
import ReactApexChart from "react-apexcharts"
import moment from "moment"
import { FaDotCircle, FaInfoCircle } from "react-icons/fa"
import {
  getSameWeekNextYear,
  getSameWeekPreviousYear,
  getWeekTooltipLabel,
} from "utils/dateUtil"

const OrderBarChart = ({ data, previousData, maxValue = 10000 }) => {
  const [showFormula, setShowFormula] = useState(false)

  // ── Union of all ISO weeks from both years ──────────────────────────────
  const allWeeks = useMemo(() => {
    const weekSet = new Set()
    data?.forEach(i => weekSet.add(moment(i.date).isoWeek()))
    previousData?.forEach(i => weekSet.add(moment(i.date).isoWeek()))
    return Array.from(weekSet).sort((a, b) => a - b)
  }, [data, previousData])

  // ── Current year maps ───────────────────────────────────────────────────
  const currentByWeek = useMemo(() => {
    const map = {}
    ;(data || []).forEach(i => {
      map[moment(i.date).isoWeek()] = Number(i.netMarginFinal) || 0
    })
    return map
  }, [data])

  const avgByWeek = useMemo(() => {
    const map = {}
    ;(data || []).forEach(i => {
      map[moment(i.date).isoWeek()] = Number(i.netMarginFinal) || 0
    })
    return map
  }, [data])

  const dateByWeek = useMemo(() => {
    const map = {}
    ;(data || []).forEach(i => {
      map[moment(i.date).isoWeek()] = moment(i.date).format("DD-MM-YYYY")
    })
    return map
  }, [data])

  // ── Previous year maps ──────────────────────────────────────────────────
  const prevByWeek = useMemo(() => {
    const map = {}
    ;(previousData || []).forEach(i => {
      map[moment(i.date).isoWeek()] = Number(i.netMarginFinal) || 0
    })
    return map
  }, [previousData])

  const prevDateByWeek = useMemo(() => {
    const map = {}
    ;(previousData || []).forEach(i => {
      map[moment(i.date).isoWeek()] = moment(i.date).format("DD-MM-YYYY")
    })
    return map
  }, [previousData])

  // ── Build series arrays ─────────────────────────────────────────────────
  const currentValues = allWeeks.map(w => currentByWeek[w] ?? null)
  const prevValues = allWeeks.map(w => prevByWeek[w] ?? null)
  const categories = allWeeks.map(w => `${w}`)

  // ── Year labels ─────────────────────────────────────────────────────────
  const currentYear = data?.length
    ? moment(data[0].date).year()
    : moment().year()

  const previousYear = previousData?.length
    ? moment(previousData[0].date).year()
    : currentYear - 1

  // ── Stats (ignore nulls) ────────────────────────────────────────────────
  const validValues = currentValues.filter(v => v !== null)
  const sum = validValues.reduce((a, b) => a + b, 0)
  const avg = validValues.length
    ? (sum / validValues.length).toFixed(0)
    : "0.00"
  const max = validValues.length ? Math.max(...validValues) : 0

  const avgRadialPercent =
    max > 0
      ? Math.min(
          Number(((Number(avg) / max) * 100).toFixed(0)),
          100
        ).toLocaleString("en-GB")
      : 0

  // ── Radial factory ──────────────────────────────────────────────────────
  const makeRadialOptions = ({
    label,
    displayValue,
    trackColor,
    nameColor,
    gradientTo,
    baseColor,
    hollowBg,
  }) => ({
    chart: {
      type: "radialBar",
      toolbar: { show: false },
      background: "transparent",
    },
    plotOptions: {
      radialBar: {
        offsetY: 0,
        startAngle: -135,
        endAngle: 135,
        hollow: { size: "62%", background: hollowBg },
        track: { background: trackColor, strokeWidth: "100%" },
        dataLabels: {
          name: {
            show: true,
            fontSize: "11px",
            fontWeight: 700,
            color: nameColor,
            offsetY: 22,
          },
          value: {
            show: true,
            fontSize: "11px",
            fontWeight: 800,
            color: "#1a1a2e",
            offsetY: -10,
            formatter: () =>
              new Intl.NumberFormat("en-GB", {
                style: "currency",
                currency: "GBP",
                minimumFractionDigits: 0,
              }).format(Number(displayValue)),
          },
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "horizontal",
        gradientToColors: [gradientTo],
        stops: [0, 100],
      },
    },
    colors: [baseColor],
    labels: [label],
    stroke: { lineCap: "round" },
  })

  const isSingleEntry = validValues.length <= 1

  const sumRadialPercent =
    max > 0
      ? Math.min(Number(((sum / max) * 100).toFixed(0)), 100).toLocaleString("en-GB")
      : 0

  const avgRadialOptions = makeRadialOptions({
    label: "Avg",
    displayValue: avg,
    hollowBg: "#fffbeb",
    trackColor: "#fde68a",
    nameColor: "#92400e",
    gradientTo: "#ef4444",
    baseColor: "#f59e0b",
  })

  const sumRadialOptions = makeRadialOptions({
    label: "Sum",
    displayValue: sum,
    hollowBg: "#f0fdf4",
    trackColor: "#bbf7d0",
    nameColor: "#166534",
    gradientTo: "#0ea5e9",
    baseColor: "#22c55e",
  })

  // ── Area chart options ──────────────────────────────────────────────────
  const areaOptions = {
    chart: {
      type: "area",
      height: 350,
      toolbar: { show: false },
      background: "transparent",
      foreColor: "#1a1a2e",
    },
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      curve: "smooth",
      width: [2.5, 2.5],
      dashArray: [0, 6],
    },
    markers: {
      size: allWeeks.length > 30 ? [2, 2] : [4, 3],
      colors: ["#fff", "#fff"],
      strokeColors: ["#f59e0b", "#6366f1"],
      strokeWidth: 2,
      hover: { size: allWeeks.length > 30 ? 4 : 6 },
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.35,
        opacityTo: 0.02,
        stops: [0, 50, 100],
        colorStops: [
          { offset: 0, color: "#f59e0b", opacity: 0.35 },
          { offset: 100, color: "#ddd", opacity: 0.02 },
        ],
      },
      opacity: [0.35, 0],
    },
    colors: ["#f59e0b", "#6366f1"],
    xaxis: {
      categories,
      overwriteCategories: categories.map((val, index) => {
        return `${index + 1}`
      }),
      axisBorder: { show: false },
      axisTicks: { show: false },
      tickAmount: allWeeks.length > 30 ? 26 : allWeeks.length,
      labels: {
        rotate: -45,
        rotateAlways: allWeeks.length > 30,
        style: {
          colors: "#1a1a2e",
          fontSize: allWeeks.length > 30 ? "9px" : "11px",
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      title: {
        text: "Net Margin (£)",
        style: { color: "#1a1a2e", fontSize: "12px", fontWeight: 700 },
      },
      labels: {
        style: { colors: "#1a1a2e", fontSize: "11px", fontWeight: 600 },
        formatter: val => `£ ${val?.toLocaleString("en-GB")}`,
      },
    },
    grid: {
      borderColor: "#e8e8f0",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: "light",
      style: { fontSize: "13px" },
      x: {
        formatter: (val, { dataPointIndex }) => {
          const weekNum = allWeeks[dataPointIndex]

          const currentDate = dateByWeek[weekNum]
          const prevDate = prevDateByWeek[weekNum]

          const resolvedCurrentDate =
            currentDate || (prevDate ? getSameWeekNextYear(prevDate) : null)
          const resolvedPrevDate =
            prevDate ||
            (currentDate ? getSameWeekPreviousYear(currentDate) : null)

          const currentLabel = getWeekTooltipLabel(
            dataPointIndex,
            resolvedCurrentDate
          )
          const prevLabel = getWeekTooltipLabel(
            dataPointIndex,
            resolvedPrevDate
          )

          return `<b>Week ${dataPointIndex + 1}</b><br/>Current Year : ${
            currentLabel || "N/A"
          }<br/>Previous Year : ${prevLabel || "N/A"}`
        },
      },
      y: {
        formatter: val =>
          val !== null && val !== undefined
            ? `£ ${Math.round(val).toLocaleString("en-GB")}`
            : "N/A",
      },
    },
    legend: { show: false },
    responsive: [
      {
        // chart container narrower than 600px
        breakpoint: 600,
        options: {
          xaxis: {
            labels: {
              rotate: -45,
              rotateAlways: true,
              style: { fontSize: "8px", fontWeight: 600 },
            },
          },
          yaxis: {
            labels: {
              style: { fontSize: "9px", fontWeight: 600 },
              formatter: val => `£${Math.round(val / 1000)}K`,
            },
          },
        },
      },
      {
        // chart container narrower than 900px
        breakpoint: 900,
        options: {
          xaxis: {
            labels: {
              rotate: -45,
              rotateAlways: allWeeks.length > 15,
              style: { fontSize: "9px", fontWeight: 600 },
            },
          },
          yaxis: {
            labels: {
              style: { fontSize: "10px", fontWeight: 600 },
              formatter: val => `£${Math.round(val / 1000)}K`,
            },
          },
        },
      },
    ],
  }

  // ── Radial card ─────────────────────────────────────────────────────────
  const RadialCard = ({ options, series, bg, border }) => (
    <div
      style={{
        background: bg,
        borderRadius: "12px",
        border: `1px solid ${border}`,
        flex: 1,
      }}
    >
      <ReactApexChart
        options={options}
        series={[Number(series)]}
        type="radialBar"
        height={160}
        width="100%"
      />
    </div>
  )

  const currentWeekSet = new Set(
    (data || []).map(i => moment(i.date).isoWeek())
  )
  const matchedPrevious = (previousData || []).filter(i =>
    currentWeekSet.has(moment(i.date).isoWeek())
  )

  const currentTotal = (data || []).reduce(
    (s, i) => s + (Number(i.netMarginFinal) || 0),
    0
  )
  const previousTotal = matchedPrevious.reduce(
    (s, i) => s + (Number(i.netMarginFinal) || 0),
    0
  )
  const diff = currentTotal - previousTotal
  const fmtGBP = v =>
    "£ " + Math.abs(v).toLocaleString("en-GB", { maximumFractionDigits: 0 })

  const currentAvg = (data || []).length
    ? currentTotal / (data || []).length
    : 0
  const previousAvg = matchedPrevious.length
    ? previousTotal / matchedPrevious.length
    : 0
  const diffPct = (((currentAvg - previousAvg) / maxValue) * 100).toFixed(1)

  return (
    <div>
      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-start mb-3 px-2 flex-wrap gap-3">
        <div>
          <h5
            className="mb-1 fw-bold"
            style={{ color: "#1a1a2e", fontSize: "16px" }}
          >
            Net Margin History
          </h5>
        </div>

        <div className="d-flex align-items-center gap-3 flex-wrap">
          {/* ── Comparison table ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "4px 24px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "10px 35px 10px 16px",
              minWidth: "300px",
              position: "relative",
            }}
          >
            {/* Formula tooltip icon */}
            <div
              style={{ position: "absolute", top: "8px", right: "10px" }}
              onMouseEnter={() => setShowFormula(true)}
              onMouseLeave={() => setShowFormula(false)}
            >
              <FaInfoCircle
                style={{
                  fontSize: "13px",
                  color: "#94a3b8",
                  cursor: "pointer",
                }}
              />
              {showFormula && (
                <div
                  style={{
                    position: "absolute",
                    top: "20px",
                    right: 0,
                    background: "#1e293b",
                    color: "#f1f5f9",
                    borderRadius: "10px",
                    padding: "12px 16px",
                    fontSize: "12px",
                    lineHeight: "1.8",
                    zIndex: 999,
                    whiteSpace: "nowrap",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                    pointerEvents: "none",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#7dd3fc",
                      marginBottom: "6px",
                      fontSize: "11px",
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                    }}
                  >
                    % Calculation
                  </div>
                  <div>
                    <span style={{ color: "#fde68a" }}>%</span> = (Weekly Avg ÷
                    Target) × 100
                  </div>
                  <div
                    style={{
                      borderTop: "1px solid #334155",
                      marginTop: "6px",
                      paddingTop: "6px",
                    }}
                  >
                    <div>
                      <span style={{ color: "#86efac" }}>Weekly Avg</span> =
                      Total Net Margin ÷ No. of Weeks
                    </div>
                    <div>
                      <span style={{ color: "#86efac" }}>Target</span> = £
                      {maxValue.toLocaleString("en-GB")}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Row 1 — Current Year */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FaDotCircle
                style={{ color: "#f59e0b", fontSize: "10px", flexShrink: 0 }}
              />
              <span style={{ fontSize: "12px", color: "#374151" }}>
                Current Year ({currentYear})
              </span>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#92400e",
                  marginLeft: "auto",
                }}
              >
                {fmtGBP(currentTotal)}
              </span>
            </div>

            {/* Row 2 — Previous Year */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                borderTop: "1px dashed #e2e8f0",
                paddingTop: "4px",
              }}
            >
              <FaDotCircle
                style={{ color: "#6366f1", fontSize: "10px", flexShrink: 0 }}
              />
              <span style={{ fontSize: "12px", color: "#374151" }}>
                Previous Year ({previousYear})
              </span>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#3730a3",
                  marginLeft: "auto",
                }}
              >
                {fmtGBP(previousTotal)}
              </span>
            </div>

            {/* Row 3 — Difference */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                borderTop: "1px dashed #e2e8f0",
                paddingTop: "4px",
              }}
            >
              <span style={{ fontSize: "12px", color: "#374151" }}>
                Difference
              </span>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: diff >= 0 ? "#15803d" : "#be123c",
                  marginLeft: "auto",
                }}
              >
                {diff >= 0 ? "+" : "-"}
                {fmtGBP(diff)}
              </span>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: diff >= 0 ? "#15803d" : "#be123c",
                }}
              >
                {diff >= 0 ? "▲" : "▼"} {Math.abs(diffPct)}%
              </span>
            </div>
          </div>

          {/* ── Avg or Sum Radial ── */}
          <div style={{ width: "160px", flexShrink: 0 }}>
            {isSingleEntry ? (
              <RadialCard
                options={sumRadialOptions}
                series={sumRadialPercent}
                bg="#f0fdf4"
                border="#bbf7d0"
              />
            ) : (
              <RadialCard
                options={avgRadialOptions}
                series={avgRadialPercent}
                bg="#fffbeb"
                border="#fde68a"
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Area Chart ── */}
      <ReactApexChart
        options={areaOptions}
        series={[
          { name: `Current Year (${currentYear})`, data: currentValues },
          { name: `Previous Year (${previousYear})`, data: prevValues },
        ]}
        type="area"
        height={320}
        width="100%"
      />

      {/* ── Legend ── */}
      <div
        className="d-flex justify-content-center gap-4 mt-2"
        style={{ borderTop: "1px solid #f0f0f0", paddingTop: "12px" }}
      >
        {[
          {
            color: "#f59e0b",
            label: `Current Year (${currentYear})`,
            type: "line",
          },
          {
            color: "#6366f1",
            label: `Previous Year (${previousYear})`,
            type: "dash",
          },
        ].map((l, i) => (
          <div key={i} className="d-flex align-items-center gap-2">
            {l.type === "line" ? (
              <span
                style={{
                  width: 16,
                  height: 2,
                  background: l.color,
                  display: "inline-block",
                }}
              />
            ) : (
              <span
                style={{
                  width: 16,
                  height: 2,
                  borderTop: `2px dashed ${l.color}`,
                  display: "inline-block",
                }}
              />
            )}
            <small
              style={{ color: "#1a1a2e", fontWeight: 600, fontSize: "11px" }}
            >
              {l.label}
            </small>
          </div>
        ))}
      </div>
    </div>
  )
}

export default OrderBarChart
